import axios from "axios";
import { getToken } from "./auth";

const API_URL = process.env.REACT_APP_API_BASE_URL;

// Axios instance with base URL
const axiosInstance = axios.create({
  baseURL: API_URL,
});

// Automatically attach JWT token
axiosInstance.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Clear auth data
      const Cookies = require("js-cookie").default;
      Cookies.remove("auth_token");
      Cookies.remove("user");
      // Redirect to login
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

/**
 * Unified response handler
 */
const handleResponse = (res, actionDesc = "Request") => {
  const { success, message, ...rest } = res.data || {};
  return {
    success,
    message: message || `${actionDesc} completed`,
    ...rest,
  };
};

/**
 * Get sales header by order number
 * @param {string} orderNo
 */
export const getSalesHeaderByNo = async (orderNo) => {
  try {
    const res = await axiosInstance.get("/Orders/GetSalesHeaderByNo", {
      params: { orderNo },
    });
    return handleResponse(res, "Get sales header");
  } catch (err) {
    console.error(`Error fetching sales header ${orderNo}:`, err);
    throw err;
  }
};

/**
 * Get all sales headers with pagination and optional search and status filter
 * @param {number} pageNumber - Current page number
 * @param {number} pageSize - Number of items per page
 * @param {string} [search] - Optional search term
 * @param {string} [status] - Optional status filter (send as enum name: "New", "AssignedToAgent", "AssignedToController", "Finished")
 */
export const getSalesHeaders = async (pageNumber, pageSize, search, status) => {
  try {
    const params = { pageNumber, pageSize };
    if (search) params.search = search;
    // Only add status if it's not "All"
    if (status && status !== "All") params.search = status;

    const res = await axiosInstance.get("/Orders/GetSalesHeaders", { params });
    return handleResponse(res, "Get sales headers");
  } catch (err) {
    console.error("Error fetching sales headers:", err);
    throw err;
  }
};

/**
 * Get orders by status
 * @param {number} status - SalesHeaderStatus (0, 1, 2, or 3)
 */
export const getOrdersByStatus = async (status) => {
  try {
    const res = await axiosInstance.get("/Orders/GetOrdersByStatus", {
      params: { status },
    });
    return handleResponse(res, "Get orders by status");
  } catch (err) {
    console.error(`Error fetching orders with status ${status}:`, err);
    throw err;
  }
};

/**
 * Assign an order to a worker
 * @param {string} orderNo
 * @param {string} workerUsername
 */
export const assignOrderToWorker = async (orderNo, workerUsername) => {
  try {
    const res = await axiosInstance.put("/Orders/AssignOrderToWorker", null, {
      params: { orderNo, workerUsername },
    });
    return handleResponse(res, "Assign order");
  } catch (err) {
    console.error(
      `Error assigning order ${orderNo} to ${workerUsername}:`,
      err
    );
    throw err;
  }
};

/**
 * Unassign worker from an order
 * @param {string} orderNo
 */
export const unassignWorker = async (orderNo) => {
  try {
    const res = await axiosInstance.put("/Orders/UnassignWorker", null, {
      params: { orderNo },
    });
    return handleResponse(res, "Unassign worker");
  } catch (err) {
    console.error(`Error unassigning worker from ${orderNo}:`, err);
    throw err;
  }
};

/**
 * Upload Excel Order (Base64)
 * @param {object} data - UploadExcelBase64Request
 * @param {string} [data.customerNo] - Customer number
 * @param {string} [data.fileBase64] - Base64 string of Excel file
 * @param {string} data.orderDate - Order date (YYYY-MM-DD)
 * @param {string} data.shipmentDate - Shipment date (YYYY-MM-DD)
 */
export const uploadExcelOrderBase64 = async (data) => {
  try {
    const res = await axiosInstance.post(
      "/Orders/UploadExcelOrderBase64",
      data
    );
    return handleResponse(res, "Upload Excel Order");
  } catch (err) {
    console.error("Error uploading Excel order:", err);
    throw err;
  }
};
